import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../store';
import api from '../../utils/api';

interface Category {
  id: number;
  name: string;
  slug: string;
  sortOrder: number;
}

interface ScannedBook {
  title: string;
  author: string;
  description?: string;
  filePath: string;
  format: string;
  cover?: string;
  subject?: string; // 从EPUB提取的分类/主题
  status: 'pending' | 'success' | 'error';
  error?: string;
  selected: boolean;
  category: number; // 使用sortOrder作为值
}

const AdminImport: React.FC = () => {
  const history = useHistory();
  const { user } = useSelector((state: RootState) => state.auth);
  
  // 标签页：local=本地扫描上传, server=服务器入库
  const [activeTab, setActiveTab] = useState<'local' | 'server'>('local');
  
  const [directoryPath, setDirectoryPath] = useState('');
  const [serverDirectoryPath, setServerDirectoryPath] = useState('');
  const [scanning, setScanning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [books, setBooks] = useState<ScannedBook[]>([]);
  const [serverBooks, setServerBooks] = useState<ScannedBook[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [message, setMessage] = useState('');
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null);
  
  // 一键入库状态
  const [scanAndImporting, setScanAndImporting] = useState(false);
  const [scanAndImportProgress, setScanAndImportProgress] = useState('');

  // 获取分类列表
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      // 按sortOrder排序
      const sorted = Array.isArray(data) ? data.sort((a: Category, b: Category) => a.sortOrder - b.sortOrder) : [];
      setCategories(sorted);
    } catch (err) {
      console.error('获取分类失败:', err);
    }
  };

  // 根据subject匹配分类
  const matchCategory = (subject: string | undefined, categoryList: Category[]): number => {
    if (!subject || categoryList.length === 0) {
      // 找"其他"分类的id
      const otherCategory = categoryList.find(cat => cat.slug === 'other' || cat.name === '其它');
      return otherCategory ? otherCategory.id : 0;
    }
    
    const subjectLower = subject.toLowerCase().trim();
    
    // 尝试匹配
    const matched = categoryList.find(cat => {
      const nameLower = cat.name.toLowerCase();
      const slugLower = cat.slug.toLowerCase();
      return subjectLower.includes(nameLower) || 
             nameLower.includes(subjectLower) ||
             subjectLower.includes(slugLower) ||
             slugLower.includes(subjectLower);
    });
    
    if (matched) {
      return matched.id;
    }
    
    // 没匹配到，使用"其他"分类
    const otherCategory = categoryList.find(cat => cat.slug === 'other' || cat.name === '其它');
    return otherCategory ? otherCategory.id : 0;
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      alert('需要管理员权限');
      history.push('/');
    }
  }, [user, history]);

  const handleScan = async () => {
    if (!directoryPath.trim()) {
      setMessage('请输入目录路径');
      return;
    }

    setScanning(true);
    setMessage('正在扫描目录...');
    setBooks([]);
    setImportResult(null);

    try {
      const { data } = await api.post('/books/batch-scan', {
        directoryPath: directoryPath.trim(),
      });

      if (data.books && data.books.length > 0) {
        // 添加 selected 和 category 字段，使用matchCategory自动匹配
        const booksWithSelection = data.books.map((book: any) => ({
          ...book,
          selected: true,
          category: matchCategory(book.subject, categories),
        }));
        setBooks(booksWithSelection);
        setMessage(`扫描完成，发现 ${booksWithSelection.length} 本图书`);
      } else {
        setMessage('未找到图书文件');
      }
    } catch (error: any) {
      console.error('扫描失败:', error);
      setMessage(`扫描失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setScanning(false);
    }
  };

  const handleImport = async () => {
    const selectedBooks = books.filter(b => b.selected && b.status !== 'error');
    
    if (selectedBooks.length === 0) {
      setMessage('请选择要导入的图书');
      return;
    }

    setImporting(true);
    setMessage('正在导入...');

    try {
      const { data } = await api.post('/books/batch-import', {
        books: selectedBooks.map(b => ({
          title: b.title,
          author: b.author,
          description: b.description,
          filePath: b.filePath,
          format: b.format,
          cover: b.cover,
          subject: b.subject, // EPUB元数据中的分类
          categoryId: b.category, // 用户选择的分类ID（优先级更高）
        })),
      });

      setImportResult({ success: data.success, failed: data.failed });
      setMessage(`导入完成：成功 ${data.success} 本，失败 ${data.failed} 本`);
      
      // 更新本地状态
      setBooks(books.map(book => {
        const result = data.results.find((r: any) => r.filePath === book.filePath);
        if (result) {
          return { ...book, status: result.status, error: result.error };
        }
        return book;
      }));
    } catch (error: any) {
      console.error('导入失败:', error);
      setMessage(`导入失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const toggleSelectAll = () => {
    const allSelected = books.every(b => b.selected);
    setBooks(books.map(b => ({ ...b, selected: !allSelected })));
  };

  const toggleBookSelection = (index: number) => {
    const newBooks = [...books];
    newBooks[index].selected = !newBooks[index].selected;
    setBooks(newBooks);
  };

  const updateBookCategory = (index: number, category: number) => {
    const newBooks = [...books];
    newBooks[index].category = category;
    setBooks(newBooks);
  };

  // 服务器入库：扫描目录
  const handleServerScan = async () => {
    if (!serverDirectoryPath.trim()) {
      setMessage('请输入服务器目录路径');
      return;
    }

    setScanning(true);
    setMessage('正在扫描服务器目录（这可能需要较长时间）...');
    setServerBooks([]);
    setImportResult(null);

    try {
      const { data } = await api.post('/books/server-scan', {
        directoryPath: serverDirectoryPath.trim(),
      });

      if (data.books && data.books.length > 0) {
        const booksWithSelection = data.books.map((book: any) => ({
          ...book,
          selected: true,
          category: matchCategory(book.subject, categories),
        }));
        setServerBooks(booksWithSelection);
        setMessage(`扫描完成，发现 ${booksWithSelection.length} 本新图书（已自动排除已入库的图书）`);
      } else {
        setMessage('未找到新的图书文件（可能都已已入库）');
      }
    } catch (error: any) {
      console.error('扫描失败:', error);
      setMessage(`扫描失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setScanning(false);
    }
  };

  // 服务器入库：导入
  const handleServerImport = async () => {
    const selectedBooks = serverBooks.filter(b => b.selected && b.status !== 'error');
    
    if (selectedBooks.length === 0) {
      setMessage('请选择要导入的图书');
      return;
    }

    setImporting(true);
    setMessage('正在入库...');

    try {
      const { data } = await api.post('/books/server-import', {
        books: selectedBooks.map(b => ({
          title: b.title,
          author: b.author,
          description: b.description,
          filePath: b.filePath,
          format: b.format,
          cover: b.cover,
          subject: b.subject,
          categoryId: b.category,
        })),
      });

      setImportResult({ success: data.success, failed: data.failed });
      setMessage(`入库完成：成功 ${data.success} 本，失败 ${data.failed} 本`);
      
      setServerBooks(serverBooks.map(book => {
        const result = data.results.find((r: any) => r.filePath === book.filePath);
        if (result) {
          return { ...book, status: result.status, error: result.error };
        }
        return book;
      }));
    } catch (error: any) {
      console.error('入库失败:', error);
      setMessage(`入库失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setImporting(false);
    }
  };

  // 服务器入库：全选
  const toggleServerSelectAll = () => {
    const allSelected = serverBooks.every(b => b.selected);
    setServerBooks(serverBooks.map(b => ({ ...b, selected: !allSelected })));
  };

  // 服务器入库：选择图书
  const toggleServerBookSelection = (index: number) => {
    const newBooks = [...serverBooks];
    newBooks[index].selected = !newBooks[index].selected;
    setServerBooks(newBooks);
  };

  // 服务器入库：修改分类
  const updateServerBookCategory = (index: number, category: number) => {
    const newBooks = [...serverBooks];
    newBooks[index].category = category;
    setServerBooks(newBooks);
  };

  // 一键入库：边扫描边入库
  const handleScanAndImport = async () => {
    if (!serverDirectoryPath.trim()) {
      setMessage('请输入服务器目录路径');
      return;
    }

    if (!window.confirm('确定要一键入库吗？这将边扫描边入库，每批处理完直接入库，中途崩溃不会丢失已入库的数据。')) {
      return;
    }

    setScanAndImporting(true);
    setMessage('正在一键入库...');
    setScanAndImportProgress('开始扫描...');

    try {
      const { data } = await api.post('/books/server-scan-and-import', {
        directoryPath: serverDirectoryPath.trim(),
        batchSize: 50,
      }, {
        timeout: 7200000, // 2小时超时
      });

      setScanAndImportProgress(data.message || `入库完成`);
      setImportResult({ success: data.success, failed: data.failed });
      setMessage(data.message);
      
    } catch (error: any) {
      console.error('一键入库失败:', error);
      setScanAndImportProgress('');
      setMessage(`一键入库失败: ${error.response?.data?.message || error.message}`);
    } finally {
      setScanAndImporting(false);
    }
  };


  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>批量导入图书</h1>
        <button 
          className="btn btn-default"
          onClick={() => history.push('/admin/books')}
        >
          返回图书管理
        </button>
      </div>

      {/* 标签页切换 */}
      <div style={{ marginBottom: '20px', borderBottom: '1px solid #eee' }}>
        <div style={{ display: 'flex', gap: '0' }}>
          <button
            onClick={() => setActiveTab('local')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'local' ? '#1890ff' : 'transparent',
              color: activeTab === 'local' ? '#fff' : '#666',
              border: 'none',
              borderBottom: activeTab === 'local' ? 'none' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              borderRadius: '4px 4px 0 0',
            }}
          >
            本地扫描上传
          </button>
          <button
            onClick={() => setActiveTab('server')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'server' ? '#1890ff' : 'transparent',
              color: activeTab === 'server' ? '#fff' : '#666',
              border: 'none',
              borderBottom: activeTab === 'server' ? 'none' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              borderRadius: '4px 4px 0 0',
            }}
          >
            服务器图书入库
          </button>
        </div>
      </div>

      {/* 本地扫描上传 */}
      {activeTab === 'local' && (
        <>
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '16px' }}>扫描目录</h3>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="输入图书目录路径，例如: D:/Books 或 /home/user/books"
                value={directoryPath}
                onChange={(e) => setDirectoryPath(e.target.value)}
                className="form-input"
                style={{ flex: 1 }}
              />
              <button 
                onClick={handleScan}
                disabled={scanning}
                className="btn btn-primary"
              >
                {scanning ? '扫描中...' : '开始扫描'}
              </button>
            </div>
            <p style={{ fontSize: '14px', color: '#666' }}>
              支持格式: EPUB, PDF, MOBI, AZW3, TXT, FB2, DOCX。文件会被复制到系统目录。
            </p>
          </div>

          {/* 消息提示 */}
          {message && !activeTab && (
            <div 
              className="card" 
              style={{ 
                marginBottom: '20px',
                background: message.includes('失败') ? '#fff2f0' : message.includes('成功') ? '#f6ffed' : '#e6f7ff',
                border: `1px solid ${message.includes('失败') ? '#ffccc7' : message.includes('成功') ? '#b7eb8f' : '#91d5ff'}`
              }}
            >
              {message}
            </div>
          )}

          {/* 扫描结果列表 */}
          {books.length > 0 && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3>扫描结果 ({books.length} 本)</h3>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={toggleSelectAll} className="btn btn-default">
                    全选/取消全选
                  </button>
                  <button 
                    onClick={handleImport}
                    disabled={importing}
                    className="btn btn-primary"
                  >
                    {importing ? '导入中...' : `导入所选 (${books.filter(b => b.selected).length})`}
                  </button>
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #eee' }}>
                    <th style={{ padding: '12px', textAlign: 'left', width: '40px' }}>
                      <input 
                        type="checkbox" 
                        checked={books.every(b => b.selected)}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>文件名</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>作者</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>格式</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>EPUB分类</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>分类</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {books.map((book, index) => (
                    <tr 
                      key={index} 
                      style={{ 
                        borderBottom: '1px solid #f0f0f0',
                        background: book.status === 'error' ? '#fff2f0' : book.status === 'success' ? '#f6ffed' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '12px' }}>
                        <input 
                          type="checkbox"
                          checked={book.selected}
                          onChange={() => toggleBookSelection(index)}
                          disabled={book.status === 'error'}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div>{book.title}</div>
                        <div style={{ fontSize: '12px', color: '#999' }}>{book.filePath}</div>
                        {book.error && (
                          <div style={{ fontSize: '12px', color: '#ff4d4f' }}>{book.error}</div>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>{book.author || '未知'}</td>
                      <td style={{ padding: '12px' }}>{book.format?.toUpperCase()}</td>
                      <td style={{ padding: '12px' }}>{book.subject || '-'}</td>
                      <td style={{ padding: '12px' }}>
                        <select
                          value={book.category}
                          onChange={(e) => updateBookCategory(index, Number(e.target.value))}
                          className="form-input"
                          style={{ width: '120px', padding: '4px' }}
                        >
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {book.status === 'pending' && <span style={{ color: '#999' }}>待导入</span>}
                        {book.status === 'success' && <span style={{ color: '#52c41a' }}>✓ 已导入</span>}
                        {book.status === 'error' && <span style={{ color: '#ff4d4f' }}>✗ 失败</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* 服务器图书入库 */}
      {activeTab === 'server' && (
        <>
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '16px' }}>服务器目录</h3>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="输入服务器图书目录路径，例如: Z:/Books 或 /mnt/books"
                value={serverDirectoryPath}
                onChange={(e) => setServerDirectoryPath(e.target.value)}
                className="form-input"
                style={{ flex: 1 }}
              />
              <button 
                onClick={handleServerScan}
                disabled={scanning || scanAndImporting}
                className="btn btn-default"
              >
                {scanning ? '扫描中...' : '扫描'}
              </button>
              <button 
                onClick={handleScanAndImport}
                disabled={scanning || scanAndImporting}
                className="btn btn-primary"
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
              >
                {scanAndImporting ? '一键入库中...' : '一键入库'}
              </button>
            </div>
            <p style={{ fontSize: '14px', color: '#666' }}>
              <strong>扫描</strong>：先扫描再入库，分两步。<br/>
              <strong>一键入库</strong>：边扫描边入库，每批处理完直接入库，崩溃不会丢失进度。（推荐大批量时使用）
            </p>
            
            {/* 一键入库进度显示 */}
            {scanAndImporting && (
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                background: '#f6ffed', 
                border: '1px solid #b7eb8f',
                borderRadius: '4px',
              }}>
                <p style={{ margin: 0, color: '#52c41a' }}>
                  🔄 {scanAndImportProgress}
                </p>
              </div>
            )}
          </div>

          {/* 服务器扫描结果列表 */}
          {serverBooks.length > 0 && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3>扫描结果 ({serverBooks.length} 本新图书)</h3>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={toggleServerSelectAll} className="btn btn-default">
                    全选/取消全选
                  </button>
                  <button 
                    onClick={handleServerImport}
                    disabled={importing}
                    className="btn btn-primary"
                  >
                    {importing ? '入库中...' : `入库所选 (${serverBooks.filter(b => b.selected).length})`}
                  </button>
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #eee' }}>
                    <th style={{ padding: '12px', textAlign: 'left', width: '40px' }}>
                      <input 
                        type="checkbox" 
                        checked={serverBooks.every(b => b.selected)}
                        onChange={toggleServerSelectAll}
                      />
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>书名</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>作者</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>格式</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>EPUB分类</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>分类</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {serverBooks.map((book, index) => (
                    <tr 
                      key={index} 
                      style={{ 
                        borderBottom: '1px solid #f0f0f0',
                        background: book.status === 'error' ? '#fff2f0' : book.status === 'success' ? '#f6ffed' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '12px' }}>
                        <input 
                          type="checkbox"
                          checked={book.selected}
                          onChange={() => toggleServerBookSelection(index)}
                          disabled={book.status === 'error'}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div>{book.title}</div>
                        <div style={{ fontSize: '12px', color: '#999' }}>{book.filePath}</div>
                        {book.error && (
                          <div style={{ fontSize: '12px', color: '#ff4d4f' }}>{book.error}</div>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>{book.author || '未知'}</td>
                      <td style={{ padding: '12px' }}>{book.format?.toUpperCase()}</td>
                      <td style={{ padding: '12px' }}>{book.subject || '-'}</td>
                      <td style={{ padding: '12px' }}>
                        <select
                          value={book.category}
                          onChange={(e) => updateServerBookCategory(index, Number(e.target.value))}
                          className="form-input"
                          style={{ width: '120px', padding: '4px' }}
                        >
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {book.status === 'pending' && <span style={{ color: '#999' }}>待入库</span>}
                        {book.status === 'success' && <span style={{ color: '#52c41a' }}>✓ 已入库</span>}
                        {book.status === 'error' && <span style={{ color: '#ff4d4f' }}>✗ 失败</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* 消息提示（通用） */}
      {message && (
        <div 
          className="card" 
          style={{ 
            marginTop: '20px',
            background: message.includes('失败') ? '#fff2f0' : message.includes('成功') ? '#f6ffed' : '#e6f7ff',
            border: `1px solid ${message.includes('失败') ? '#ffccc7' : message.includes('成功') ? '#b7eb8f' : '#91d5ff'}`
          }}
        >
          {message}
        </div>
      )}

      {/* 导入结果 */}
      {importResult && (
        <div className="card" style={{ marginTop: '20px' }}>
          <h3>{activeTab === 'local' ? '导入结果' : '入库结果'}</h3>
          <div style={{ display: 'flex', gap: '40px', marginTop: '16px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', color: '#52c41a' }}>{importResult.success}</div>
              <div style={{ color: '#666' }}>{activeTab === 'local' ? '导入成功' : '入库成功'}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', color: '#ff4d4f' }}>{importResult.failed}</div>
              <div style={{ color: '#666' }}>{activeTab === 'local' ? '导入失败' : '入库失败'}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminImport;
